import { useMemo, useState } from 'react'
import { CheckIcon, ChevronsUpDownIcon } from 'lucide-react'

import { useListTagsQuery, type TagRecord } from '@axilon-apps/domains-ontology-ui-state'

import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover'
import { cn } from '../../lib/utils'
import { useDebouncedValue } from '../../lib/use-debounced-value'

/** How many matching tags to show in the dropdown at once. */
const TAG_PICKER_PAGE_SIZE = 50

/**
 * Compose the AIP-160 filter string the proxy's ListTags expects for a
 * tag-path substring search. Mirrors TagsFilterBar.composeFilter's
 * `tag_path:"<text>"` clause. Pure so the substring → filter mapping can be
 * tested without React. Empty input → empty filter (lists the first page).
 */
export function composeTagPathFilter(search: string): string {
  const trimmed = search.trim()
  if (!trimmed) return ''
  const escaped = trimmed.replace(/"/g, '\\"')
  return `tag_path:"${escaped}"`
}

export interface RegistryTagPickerProps {
  /** Selected registry tag id (UUID), or '' when nothing is picked. */
  value: string
  /** Called with the chosen registry tag id and its resolved tag path. */
  onChange: (tagId: string, tagPath: string) => void
  /**
   * Resolved tag path for the current `value`, used to label the trigger when
   * the selected tag is not in the visible page (e.g. when editing an existing
   * config). Falls back to the id when absent.
   */
  selectedTagPath?: string
  /**
   * When set, only these registry ids are offered in the dropdown (intersection
   * with the ListTags page). Used by ConditionBuilder when the form scopes
   * leaves to a pre-selected tag set. Omit / empty = browse the full registry.
   */
  availableTagIds?: readonly string[]
  placeholder?: string
  invalid?: boolean
  disabled?: boolean
}

/**
 * Searchable registry-tag selector backed by `useListTagsQuery`. Searches by
 * tag-path prefix/substring; the picker's value is the tag's registry id and
 * it renders the tag path. Condition triggers reference tags by registry id, so
 * this replaces the old free-text tag-path input.
 */
export function RegistryTagPicker({
  value,
  onChange,
  selectedTagPath,
  availableTagIds,
  placeholder = 'Select a tag…',
  invalid,
  disabled
}: RegistryTagPickerProps) {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  // Debounce so typing doesn't issue a ListTags request per keystroke.
  const debounced = useDebouncedValue(search, 250)

  const filter = useMemo(() => composeTagPathFilter(debounced), [debounced])
  const { data, isLoading, error } = useListTagsQuery({
    filter,
    pageSize: TAG_PICKER_PAGE_SIZE
  })

  const availableSet = useMemo(() => {
    if (!availableTagIds || availableTagIds.length === 0) return null
    return new Set(availableTagIds)
  }, [availableTagIds])

  const tags: TagRecord[] = useMemo(() => {
    const page = data?.tags ?? []
    if (!availableSet) return page
    return page.filter((t) => availableSet.has(t.id))
  }, [data?.tags, availableSet])

  // Label the trigger by the resolved path when we have one (from the prop or
  // a tag in the current page), else fall back to the raw id.
  const selectedFromPage = tags.find((t) => t.id === value)?.tagPath
  const triggerLabel = value ? (selectedTagPath ?? selectedFromPage ?? value) : placeholder

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          aria-invalid={invalid ? true : undefined}
          disabled={disabled}
          className="h-8 w-full justify-between text-xs font-normal"
        >
          <span className={cn('truncate', !value && 'text-ink-4')}>{triggerLabel}</span>
          <ChevronsUpDownIcon className="ml-2 size-3.5 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
        <div className="flex flex-col">
          <div className="border-b border-line p-2">
            <Input
              autoFocus
              aria-label="Search tags by path"
              placeholder="Search tag path…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-8 text-xs"
            />
          </div>
          <ul className="m-0 max-h-64 list-none overflow-auto p-1">
            {isLoading && <li className="px-2 py-1.5 text-xs text-ink-4">Loading…</li>}
            {!isLoading && error && (
              <li className="px-2 py-1.5 text-xs text-st-down">Failed to load tags.</li>
            )}
            {!isLoading && !error && tags.length === 0 && (
              <li className="px-2 py-1.5 text-xs text-ink-4">No matching tags.</li>
            )}
            {tags.map((tag) => (
              <li key={tag.id}>
                <button
                  type="button"
                  onClick={() => {
                    onChange(tag.id, tag.tagPath)
                    setOpen(false)
                  }}
                  className={cn(
                    'flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-xs',
                    'hover:bg-accent/40 focus-visible:bg-accent/40 focus-visible:outline-none'
                  )}
                >
                  <CheckIcon
                    className={cn(
                      'size-3.5 shrink-0',
                      tag.id === value ? 'opacity-100' : 'opacity-0'
                    )}
                  />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate font-mono">{tag.tagPath}</span>
                    {tag.sourceUrl && (
                      <span className="block truncate text-10 text-ink-4">{tag.sourceUrl}</span>
                    )}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      </PopoverContent>
    </Popover>
  )
}
